import { ReactNode, ButtonHTMLAttributes } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'glass';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'glass',
  size = 'md',
  isLoading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-sm border border-transparent transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

  const variantClasses = {
    // Solid white foreground button - "secondary" in web-2
    primary:
      'bg-foreground text-primary hover:bg-white cursor-pointer',
    secondary:
      'bg-white/10 text-foreground hover:bg-white/20 cursor-pointer',
    outline:
      'border-white/20 bg-white/5 text-foreground hover:bg-white/10 cursor-pointer',
    ghost:
      'text-foreground hover:bg-white/10 cursor-pointer',
    // Web-2 glass: backdrop-blur, white border, serif
    glass:
      'backdrop-blur-sm border-white/80 font-serif bg-white/5 hover:bg-foreground hover:text-primary cursor-pointer',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-5 py-2.5 text-base gap-2',
    lg: 'px-8 py-3.5 text-lg gap-2.5',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <LoadingSpinner size="sm" />}
      {children}
    </button>
  );
}
